import { ApiError } from "@/shared/api/errors";
import type { Agent, ModelSettings } from "@/shared/types/agent";
import type { ApiModelChoice, ModelOption, ModelSelection, ModelsWorkspace, ProviderId } from "@/shared/types/models";
import { PROVIDERS, isSubscription } from "@/shared/types/models";

export const selectionOf = (agent: Pick<Agent, "model" | "modelSettings">): ModelSelection =>
  agent.modelSettings?.selection ?? { modelId: agent.modelSettings?.model || agent.model, source: "managed" };
export const selectionKey = (s: ModelSelection): string => JSON.stringify([s.modelId, s.source, s.source === "connection" ? s.connectionId : null]);

/** The wire's pair as a selection. No connection means Definable's own models. */
export const toSelection = ({ model, connection_id }: ApiModelChoice): ModelSelection =>
  connection_id ? { modelId: model, source: "connection", connectionId: connection_id } : { modelId: model, source: "managed" };
/** A selection as the pair agents and the workspace default send. */
export const toModelChoice = (s: ModelSelection): ApiModelChoice =>
  ({ model: s.modelId, connection_id: s.source === "connection" ? s.connectionId : null });

/** Which models a picker offers, and what it says when none fits. */
export interface ModelFilter { fits: (model: ModelOption) => boolean; empty: string }
export const FAST_MODEL_FILTER: ModelFilter = { fits: (model) => model.tier === "fast", empty: "Your connections have no fast models" };
/** Beside a fast model the main one must be slow (the service refuses two fast ones). */
export const mainModelFilter = (agent: Pick<Agent, "modelSettings">): ModelFilter | undefined =>
  fastSelectionOf(agent) ? { fits: (model) => model.tier !== "fast", empty: "Your connections have no slow models" } : undefined;

/** An agent row's fast model as a selection, or null when it has none. */
export const toFastSelection = (row: { fast_model?: string | null; fast_connection_id?: string | null }): ModelSelection | null =>
  row.fast_model ? toSelection({ model: row.fast_model, connection_id: row.fast_connection_id ?? null }) : null;
export const fastSelectionOf = (agent: Pick<Agent, "modelSettings">): ModelSelection | null => agent.modelSettings?.fastSelection ?? null;
/** The agent with this fast model, or none. */
export const selectFastModel = (agent: Agent, fastSelection: ModelSelection | null): Agent =>
  ({ ...agent, modelSettings: { ...agent.modelSettings, fastSelection } });

const connectionOf = (s: ModelSelection, data?: ModelsWorkspace) =>
  s.source === "connection" ? data?.connections.find((c) => c.id === s.connectionId) : undefined;

/** The selected model's facts. An id is unique only within a provider, so the connection's provider narrows it. */
export function modelOf(s: ModelSelection, data?: ModelsWorkspace): ModelOption | undefined {
  // only Definable's own list: a connection's row would name its account (Claude Code, Codex)
  if (s.source === "managed") return data?.definable.find((m) => m.id === s.modelId);
  const provider = connectionOf(s, data)?.provider;
  return data?.models.find((m) => m.id === s.modelId && (!provider || m.provider === provider));
}

/** Whose model it is, for its mark: the model's provider, else its connection's. */
export function providerOf(s: ModelSelection, data?: ModelsWorkspace): ProviderId | null {
  return modelOf(s, data)?.provider ?? connectionOf(s, data)?.provider ?? null;
}

export function selectionLabel(s: ModelSelection | null, data?: ModelsWorkspace): string {
  if (!s) return "No model chosen";
  const model = modelOf(s, data);
  const name = model?.name ?? s.modelId;
  // Definable's model by its maker (Anthropic, OpenAI), never the account it runs on
  if (s.source === "managed") return `${name} · ${model ? `${PROVIDERS[model.provider]} · ` : ""}Definable`;
  const connection = connectionOf(s, data);
  return `${name} · ${connection ? `${PROVIDERS[connection.provider]} / ${connection.name}` : "Unavailable connection"}`;
}

/** Why a selection can't run, or null when it can — the backend's rule
    (`backend/modules/providers/business/model_choice.py`): a model on no connection is
    the backend's to judge (it routes it, routes.py); on a connection, a live connection of
    this workspace whose key isn't rejected and a model it lists that has facts.
    `unavailable` passes: an outage says nothing about the key. Components ask
    through `useModelCheck`, never this directly. */
export function selectionProblem(s: ModelSelection | null, data: ModelsWorkspace): string | null {
  if (!s) return data.connections.length || data.definable.length ? "Choose a model." : "Connect a provider to choose a model.";
  // A model on no connection is the backend's to judge: it checked it when it was chosen and
  // says why at run time if it can't run now. The Definable list is what pickers offer, not
  // what runs: it misses a model for a while after a listing outage, a dated id, a deprecated
  // one, and a family the platform key runs (PR #315 review).
  if (s.source === "managed") return null;
  const connection = connectionOf(s, data);
  if (!connection) return "That connection was removed. Pick a model from your connections.";
  if (isSubscription(connection.provider) && ["invalid", "restricted"].includes(connection.status)) return `Reconnect the subscription on “${connection.name}” in Settings → Models & providers.`;
  if (connection.status === "invalid") return `The key on “${connection.name}” was rejected. Replace it, or pick another model.`;
  if (connection.status === "restricted") return `The key on “${connection.name}” can’t run models. Replace it, or pick another model.`;
  return connection.modelIds.includes(s.modelId) && modelOf(s, data) ? null : `“${connection.name}” doesn’t offer this model. Pick another.`;
}

/** An API refusal as a form says it: the envelope's message, which the backend
    writes for this screen — except a validation failure, whose issue says more
    than "Validation failed.", and a member's write. */
export function connectionError(err: unknown): string {
  if (!(err instanceof ApiError)) return err instanceof Error ? err.message : "Something went wrong.";
  const issue = err.details?.map((d) => d.issue).find((i): i is string => typeof i === "string");
  if (err.code === 1010004 && issue) return issue.charAt(0).toUpperCase() + issue.slice(1);
  if (err.code === 1020002) return "Only workspace owners and admins can manage connections.";
  return err.message;
}

export function settingsForSelection(current: ModelSettings, selection: ModelSelection, model?: ModelOption): ModelSettings {
  return {
    ...current, model: selection.modelId, selection,
    maxTokens: Math.min(current.maxTokens, model?.maxOutputTokens ?? current.maxTokens),
    temperature: model?.temperature ? Math.max(model.temperature.min, Math.min(current.temperature, model.temperature.max)) : current.temperature,
  };
}
export function selectAgentModel(agent: Agent, selection: ModelSelection, model?: ModelOption): Agent {
  return { ...agent, model: selection.modelId, modelSettings: settingsForSelection(agent.modelSettings, selection, model) };
}
